import * as React from 'react'

import { ArrowLeft, ChevronDown, ChevronRight, X } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { ProjectAccessType } from '~/client/graph'
import BaseObservableTable from '~/client/src/desktop/components/BaseObservableTable/BaseObservableTable'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import {
  ILWFCCategory,
  LWFCRowData,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { getAccountTypeTranslate } from '~/client/src/shared/constants/ProjectRoles'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import {
  IProcoreCompanyWithProjectsAndUsers,
  IProcoreProjectWithUsers,
} from '~/client/src/shared/services/ProcoreService'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'

import { DataKeys } from '../../ProjectMembersList.store'
import ProcoreDirectoryDialogStore, {
  IProcoreUserView,
} from './ProcoreDirectoryDialog.store'

import './ProcoreDirectoryDialog.scss'

interface IProps {
  isOpen: boolean
  closeDialog: () => void
  addToProject: (users: User[]) => void
  openAlert: (message: string) => void
  loading: boolean
  procoreCompanies: IProcoreCompanyWithProjectsAndUsers[]

  state?: DesktopInitialState
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
}

@inject('state', 'userProjectsStore', 'projectMembersStore')
@observer
export default class ProcoreDirectoryDialog extends React.Component<IProps> {
  private readonly store: ProcoreDirectoryDialogStore

  @observable
  private selectedCompany: IProcoreCompanyWithProjectsAndUsers = null
  @observable private selectedProject: IProcoreProjectWithUsers = null

  private get title() {
    const { closeDialog, loading } = this.props

    let titleText: string
    let shouldShowAddButton = false
    if (this.selectedProject) {
      titleText = this.selectedProject.display_name
      shouldShowAddButton = true
    } else if (this.selectedCompany) {
      titleText = Localization.translator.selectProcoreProject
    } else {
      titleText = Localization.translator.selectProcoreCompany
    }

    return (
      <div className="tw:flex tw:items-center">
        <X className="tw:size-4" onClick={closeDialog} />
        <img
          src="/static/icons/procore-icon.svg"
          style={{ height: '25px' }}
          className="mr15 ml5"
        />
        <span className="tw:flex-1 text bold extra-large">{titleText}</span>
        {shouldShowAddButton && (
          <div>
            <Button
              className="mr10 no-grow tw:w-[200px]"
              disabled={!this.store.isSomeRowSelected || loading}
              onClick={this.addSelectedUsers}
            >
              {loading ? (
                <Spinner size="xs" />
              ) : (
                Localization.translator.addXUsers(this.store.selectedUsersCount)
              )}
            </Button>
          </div>
        )}
      </div>
    )
  }

  public constructor(props: IProps) {
    super(props)

    this.store = new ProcoreDirectoryDialogStore(
      props.state,
      this.getUserViews,
      props.userProjectsStore,
    )
    makeObservable(this)
  }

  public render() {
    const { isOpen } = this.props

    return (
      <Dialog
        open={isOpen}
        onOpenChangeComplete={open => !open && this.onClosed()}
      >
        <DialogContent
          showCloseButton={false}
          className={classList({
            'procore-users-dialog tw:flex tw:h-[800px] tw:max-h-[calc(100vh-200px)] tw:max-w-[calc(100vw-200px)] tw:flex-col tw:sm:max-w-[calc(100vw-200px)]': true,
            'tw:w-[600px]': !this.selectedProject,
            'tw:w-[1130px]': !!this.selectedProject,
          })}
        >
          <DialogHeader>
            <DialogTitle render={<div />}>{this.title}</DialogTitle>
          </DialogHeader>
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
            {this.renderBackArrow()}
            <div className="content tw:overflow-auto">
              {this.renderContent()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  private renderBackArrow() {
    if (!this.selectedCompany) {
      return <div className="pb15 bb-light-grey" />
    }

    const onClick = () =>
      (this.selectedProject ? this.selectProject : this.selectCompany)(null)
    return (
      <div className="pb15 bb-light-grey">
        <div className="w-fit-content pointer" onClick={onClick}>
          <ArrowLeft className="tw:inline tw:size-4" />
          <span className="text large bold ml10">
            {Localization.translator.goBack}
          </span>
        </div>
      </div>
    )
  }

  @action.bound
  private onClosed() {
    this.selectedCompany = null
    this.selectedProject = null
  }

  private renderContent() {
    const { isOpen } = this.props
    if (!isOpen) {
      return null
    }

    if (this.selectedProject) {
      return this.renderUserTable()
    }

    if (this.selectedCompany) {
      return this.renderProjectsList()
    }

    return this.renderCompaniesList()
  }

  private renderCompaniesList() {
    const { procoreCompanies } = this.props
    if (!procoreCompanies?.length) {
      return null
    }

    return procoreCompanies.map(company => {
      return (
        <div
          key={company.name}
          onClick={this.selectCompany.bind(this, company)}
          className="procore-list-item bb-light-grey pa20 pointer"
        >
          {company.name}
        </div>
      )
    })
  }

  @action.bound
  private selectCompany(company: IProcoreCompanyWithProjectsAndUsers) {
    // A project is usable only when the directory API returned its users; a
    // company with no such project has no accessible directory at all.
    if (company && !company.projects?.some(project => project.users)) {
      this.props.openAlert(Localization.translator.procoreNoAccessibleProjects)
      return
    }
    this.selectedCompany = company
  }

  private renderProjectsList() {
    return this.selectedCompany.projects.map(project => {
      const { users } = project
      return (
        <div
          key={project.id}
          onClick={this.selectProject.bind(this, project)}
          className={classList({
            'procore-list-item bb-light-grey pa20 pointer': true,
            'inactive-element': !users,
          })}
        >
          {this.getProjectLabel(project)}
        </div>
      )
    })
  }

  private getProjectLabel(project: IProcoreProjectWithUsers) {
    const { display_name, users } = project
    const countLabel = !users
      ? Localization.translator.noDirectoryAccess
      : Localization.translator.xUsers(users.length)
    return `${display_name} (${countLabel})`
  }

  @action.bound
  private selectProject(project: IProcoreProjectWithUsers) {
    // Projects without directory users are shown inactive; block opening their
    // empty view and tell the user why instead of selecting them.
    if (project && !project.users) {
      this.props.openAlert(Localization.translator.noDirectoryAccess)
      return
    }
    this.selectedProject = project
  }

  private renderUserTable() {
    return (
      <BaseObservableTable
        store={this.store}
        autoSizerClassName="table-container mt10 project-members-upload-content"
        fixedWidth={1090}
        valueRenderer={this.renderValue}
        isRowDisabled={this.getIsRowDisabled}
        renderMainCheckbox={this.renderSelectAll}
        categoryContent={this.renderCategoryHeader}
      />
    )
  }

  private renderCategoryHeader = (
    category: ILWFCCategory,
    dataKey: string,
  ): JSX.Element => {
    if (dataKey !== DataKeys.CHECKBOX) {
      return null
    }

    const { categoryId, categoryLabel, isChecked } = category
    const CaretIcon = this.store.collapsedCategories.get(categoryId)
      ? ChevronRight
      : ChevronDown
    const checkboxColumnWidth = this.store.columns.find(
      column => column.dataKey === DataKeys.CHECKBOX,
    )?.width

    return (
      <div className="tw:absolute tw:inset-x-0 tw:flex tw:items-center">
        <span
          className="tw:flex tw:shrink-0 tw:justify-center"
          style={{ width: checkboxColumnWidth }}
          onClick={e => e.stopPropagation()}
        >
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => this.store.toggleCategory(category)}
          />
        </span>
        <CaretIcon className="tw:size-4 tw:shrink-0" />
        <span className="tw:ml-2.5 tw:shrink-0">{categoryLabel}</span>
      </div>
    )
  }

  private renderSelectAll = () => {
    return (
      <span className="tw:flex tw:justify-center tw:w-full">
        <Checkbox
          checked={this.store.isAllSelected}
          onCheckedChange={() => this.toggleAll()}
        />
      </span>
    )
  }

  private toggleAll = () => {
    if (this.store.isAllSelected) {
      this.store.unselectAll()
    } else {
      this.store.selectAll()
    }
  }

  private renderValue = (value: any, dataKey: string, data: LWFCRowData) => {
    if (dataKey === DataKeys.CHECKBOX) {
      return (
        <span className="tw:flex tw:justify-center tw:w-full">
          <Checkbox checked={value} />
        </span>
      )
    }

    if (dataKey === DataKeys.ACCOUNT_TYPE) {
      return (
        <div
          className="tw:flex tw:items-center no-select underline-hover"
          onClick={this.changeAccountType.bind(this, data)}
        >
          <ChevronDown className="tw:size-4" />
          <span className="ml10">
            {getAccountTypeTranslate(value as ProjectAccessType)}
          </span>
        </div>
      )
    }

    if (dataKey === DataKeys.PROJECTS && value) {
      return (
        <div className="row no-select underline-hover" title={value.join('\n')}>
          <span className="no-grow text large mr5">{value[0]}</span>
          {value.length > 1 && (
            <span className="no-grow text large primary-blue bold">
              {'+' + (value.length - 1)}
            </span>
          )}
        </div>
      )
    }

    return null
  }

  private getIsRowDisabled = (data: LWFCRowData) => {
    return !this.store.canSelectInstance(data.user)
  }

  private changeAccountType(
    rowData: LWFCRowData,
    event: React.MouseEvent<HTMLElement>,
  ) {
    event.stopPropagation()
    this.store.changeAccountType(
      rowData[DataKeys.ID],
      rowData[DataKeys.ACCOUNT_TYPE],
    )
  }

  private getUserViews = (): IProcoreUserView[] => {
    if (!this.selectedProject || !this.selectedProject.users) {
      return []
    }

    return this.selectedProject.users.map(user => {
      const struxhubUser = this.props.projectMembersStore.getByEmail(user.email)

      return Object.assign(
        observable({
          uid: user.email || String(user.id),
          accountType: null,
          companyName: user.vendor?.name || '',
          struxhubUser,
        }),
        user,
      )
    })
  }

  private addSelectedUsers = () => {
    const users = this.store.getSelectedUsers()
    const usersWithoutContact = users.filter(
      ({ email, phoneNumber }) => !email && !phoneNumber,
    )
    if (usersWithoutContact.length) {
      this.props.openAlert(
        Localization.translator.procoreUsersWithoutContact(
          usersWithoutContact.map(({ fullName }) => fullName),
        ),
      )
      return
    }

    this.props.addToProject(users)
  }
}
